import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { seoGovernance, seoPageLockAction, seoUpdatePolicy } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Loading, Empty, DataTable } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/rules")({
  head: () => ({ meta: [{ title: "Rules and safety · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: RulesPage,
});

function RulesPage() {
  const load = useServerFn(seoGovernance);
  const savePolicy = useServerFn(seoUpdatePolicy);
  const lockFn = useServerFn(seoPageLockAction);
  const q = useQuery({ queryKey: ["seo-governance"], queryFn: () => load() });
  const [path, setPath] = useState("");

  const update = useMutation({
    mutationFn: (patch: any) => savePolicy({ data: patch }),
    onSuccess: () => {
      toast.success("Settings saved");
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save"),
  });

  const lock = useMutation({
    mutationFn: (payload: any) => lockFn({ data: payload }),
    onSuccess: () => {
      toast.success("Updated");
      setPath("");
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not update"),
  });

  const p = (q.data as any)?.policy;
  const locks = (q.data as any)?.locks ?? [];

  return (
    <SeoPage
      title="Rules and safety"
      description="Controls what the SEO system is allowed to do on its own. Pages about rates, applying, licensing and legal terms are permanently protected and can never be changed automatically."
    >
      {q.isLoading || !p ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <Panel title="Automation controls">
            <div className="grid gap-4">
              <Toggle
                label="Pause all automatic changes"
                description="When on, every suggestion waits for your approval — nothing is applied by itself."
                checked={p.kill_switch}
                onChange={(v) => update.mutate({ kill_switch: v })}
              />
              <Toggle
                label="Allow low-risk technical fixes automatically"
                description="Things like canonical tags, indexing signals and structured data. Never wording about rates, eligibility or licensing."
                checked={p.auto_apply_low_risk}
                onChange={(v) => update.mutate({ auto_apply_low_risk: v })}
              />
              <Toggle
                label="Allow medium-risk wording automatically"
                description="Search titles and descriptions that contain no sensitive wording. Off is the safer setting."
                checked={p.allow_medium_risk_auto}
                onChange={(v) => update.mutate({ allow_medium_risk_auto: v })}
              />
              <div>
                <p className="text-sm font-semibold">Maximum changes per run</p>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  defaultValue={p.max_actions_per_run}
                  className="mt-1 max-w-[120px]"
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (v && v !== p.max_actions_per_run) update.mutate({ max_actions_per_run: v });
                  }}
                />
              </div>
            </div>
          </Panel>

          <Panel
            title="Protected pages"
            subtitle="Automation can never change these. Rates, applying, legal, licensing and admin pages are always protected."
          >
            <form
              className="mb-4 flex flex-wrap gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (path.trim()) lock.mutate({ action: "lock", path: path.trim(), reason: "Locked by admin" });
              }}
            >
              <Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/dscr-loans" className="max-w-xs" />
              <Button type="submit" size="sm">
                Protect page
              </Button>
            </form>
            {locks.length ? (
              <DataTable
                head={["Page", "Reason", "Added", ""]}
                rows={locks.map((l: any) => [
                  l.path,
                  l.reason ?? "—",
                  new Date(l.created_at).toLocaleDateString(),
                  <Button size="sm" variant="ghost" onClick={() => lock.mutate({ action: "unlock", path: l.path })}>
                    Remove
                  </Button>,
                ])}
              />
            ) : (
              <Empty>No extra pages protected — the always-protected list still applies.</Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-3">
      <div>
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}
